import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';
import { updateTicketActivity } from './ticketActivity';
import { getCachedUserAndProfile } from '../utils/userCache';

const BUCKET = 'ticket-attachments';

/**
 * 取得 Ticket 的所有交付紀錄（含附件）
 */
export const getDeliveryLogsByTicket = async (ticketId) => {
  try {
    const [logsRes, attachmentsRes] = await Promise.all([
      supabase
        .from('ticket_delivery_logs')
        .select(`
          *,
          user:user_id (id, email, name, avatar_url)
        `)
        .eq('ticket_id', ticketId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true }),
      supabase
        .from('ticket_attachments')
        .select('*')
        .eq('ticket_id', ticketId)
        .not('delivery_log_id', 'is', null),
    ]);

    if (logsRes.error) throw logsRes.error;

    // Group attachments by delivery_log_id
    const attachmentsByLogId = {};
    (attachmentsRes.data || []).forEach((att) => {
      if (!attachmentsByLogId[att.delivery_log_id]) {
        attachmentsByLogId[att.delivery_log_id] = [];
      }
      attachmentsByLogId[att.delivery_log_id].push(att);
    });

    const logs = (logsRes.data || []).map((log) => ({
      ...log,
      attachments: attachmentsByLogId[log.id] || [],
    }));

    return { data: logs, error: null };
  } catch (error) {
    console.error('Error fetching delivery logs:', error);
    return { data: null, error };
  }
};

/**
 * 新增交付紀錄
 */
export const createDeliveryLog = async (ticketId, { label, version, environment, note }) => {
  try {
    const { profile } = await getCachedUserAndProfile(supabase);
    if (!profile) throw new Error('未登入');

    // 取得 ticket 的 company_id, department_id
    const { data: ticketMeta } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', ticketId)
      .single();

    const insertData = {
      ticket_id: ticketId,
      user_id: profile.id,
      label,
      version: version || null,
      environment: environment || null,
      note: note || null,
      company_id: ticketMeta?.company_id,
      department_id: ticketMeta?.department_id,
    };

    const { data: log, error } = await supabase
      .from('ticket_delivery_logs')
      .insert(insertData)
      .select(`
        *,
        user:user_id (id, email, name, avatar_url)
      `)
      .single();

    if (error) throw error;

    logAudit('create', 'ticket_delivery_log', log.id, label, {
      after: insertData,
    }, { ticket_id: ticketId },
      ticketMeta?.department_id,
      ticketMeta?.company_id
    );

    await updateTicketActivity(ticketId, profile.id);

    return { data: { ...log, attachments: [] }, error: null };
  } catch (error) {
    console.error('Error creating delivery log:', error);
    return { data: null, error };
  }
};

/**
 * 更新交付紀錄
 */
export const updateDeliveryLog = async (logId, updates) => {
  try {
    // 取得舊資料
    const { data: oldLog, error: fetchErr } = await supabase
      .from('ticket_delivery_logs')
      .select('*')
      .eq('id', logId)
      .single();

    if (fetchErr) throw fetchErr;

    const updateData = { updated_at: new Date().toISOString() };
    if (updates.label !== undefined) updateData.label = updates.label;
    if (updates.version !== undefined) updateData.version = updates.version || null;
    if (updates.environment !== undefined) updateData.environment = updates.environment || null;
    if (updates.note !== undefined) updateData.note = updates.note || null;

    const { data: log, error } = await supabase
      .from('ticket_delivery_logs')
      .update(updateData)
      .eq('id', logId)
      .select(`
        *,
        user:user_id (id, email, name, avatar_url)
      `)
      .single();

    if (error) throw error;

    const { data: ticketMeta } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', oldLog.ticket_id)
      .single();

    logAudit('update', 'ticket_delivery_log', logId, log.label, {
      before: { label: oldLog.label, version: oldLog.version, environment: oldLog.environment, note: oldLog.note },
      after: { label: log.label, version: log.version, environment: log.environment, note: log.note },
    }, { ticket_id: oldLog.ticket_id },
      ticketMeta?.department_id,
      ticketMeta?.company_id
    );

    const { profile } = await getCachedUserAndProfile(supabase);
    await updateTicketActivity(oldLog.ticket_id, profile?.id);

    return { data: log, error: null };
  } catch (error) {
    console.error('Error updating delivery log:', error);
    return { data: null, error };
  }
};

/**
 * 刪除交付紀錄（soft delete）
 */
export const deleteDeliveryLog = async (logId) => {
  try {
    const { data: oldLog, error: fetchErr } = await supabase
      .from('ticket_delivery_logs')
      .select('*')
      .eq('id', logId)
      .single();

    if (fetchErr) throw fetchErr;

    const { error } = await supabase
      .from('ticket_delivery_logs')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', logId);

    if (error) throw error;

    const { data: ticketMeta } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', oldLog.ticket_id)
      .single();

    logAudit('delete', 'ticket_delivery_log', logId, oldLog.label, {
      before: { label: oldLog.label, version: oldLog.version, environment: oldLog.environment },
    }, { ticket_id: oldLog.ticket_id },
      ticketMeta?.department_id,
      ticketMeta?.company_id
    );

    const { profile } = await getCachedUserAndProfile(supabase);
    await updateTicketActivity(oldLog.ticket_id, profile?.id);

    return { error: null };
  } catch (error) {
    console.error('Error deleting delivery log:', error);
    return { error };
  }
};

/**
 * 上傳交付紀錄附件
 */
export const uploadDeliveryLogAttachment = async (ticketId, deliveryLogId, file) => {
  try {
    const { profile } = await getCachedUserAndProfile(supabase);
    if (!profile) throw new Error('未登入');

    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${ticketId}/delivery/${timestamp}_${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    const { data: attachment, error: dbError } = await supabase
      .from('ticket_attachments')
      .insert({
        ticket_id: ticketId,
        uploaded_by: profile.id,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type,
        delivery_log_id: deliveryLogId,
      })
      .select('*')
      .single();

    if (dbError) throw dbError;

    return { data: attachment, error: null };
  } catch (error) {
    console.error('Error uploading delivery log attachment:', error);
    return { data: null, error };
  }
};

/**
 * 取得附件簽名 URL
 */
export const getDeliveryLogAttachmentUrl = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, 3600);

    if (error) throw error;
    return { data: data.signedUrl, error: null };
  } catch (error) {
    console.error('Error getting attachment URL:', error);
    return { data: null, error };
  }
};

/**
 * 刪除交付紀錄附件
 */
export const deleteDeliveryLogAttachment = async (attachmentId, filePath) => {
  try {
    const { error: storageError } = await supabase.storage
      .from(BUCKET)
      .remove([filePath]);

    if (storageError) {
      console.warn('Storage deletion warning:', storageError);
    }

    const { error: dbError } = await supabase
      .from('ticket_attachments')
      .delete()
      .eq('id', attachmentId);

    if (dbError) throw dbError;

    return { error: null };
  } catch (error) {
    console.error('Error deleting delivery log attachment:', error);
    return { error };
  }
};
